import { Activity } from '@/services/activityService';
import ActivityCard from './ActivityCard';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

interface AssignableUser {
  id: string;
  firstName: string;
  lastName: string;
  email?: string;
}

interface ActivityTypeInfo {
  code: string;
  actionType?: 'file' | 'form' | null;
  _count?: { modules: number };
}

interface ActivityKanbanProps {
  activities: Activity[];
  users?: AssignableUser[];
  activityTypes?: ActivityTypeInfo[];
  onAssign?: (activityId: string, userId: string) => void;
  onActivityClick?: (activity: Activity) => void;
  onActionClick?: (activity: Activity, mode: 'file' | 'form') => void;
  showCheckbox?: boolean;
  selectedIds?: Set<string>;
  onSelectChange?: (activityId: string, selected: boolean) => void;
  showStatusActions?: boolean;
  onStatusChange?: (activityId: string, status: Activity['status']) => void;
  className?: string;
}

const columns: Array<{ status: Activity['status']; label: string }> = [
  { status: 'PENDING', label: 'In Attesa' },
  { status: 'IN_PROGRESS', label: 'In Corso' },
  { status: 'COMPLETED', label: 'Completate' },
  { status: 'CANCELLED', label: 'Annullate' },
];

function getEffectiveActionType(at: ActivityTypeInfo | undefined): 'file' | 'form' {
  if (at?.actionType) return at.actionType as 'file' | 'form';
  if (at?._count?.modules && at._count.modules > 0) return 'form';
  return 'file';
}

export default function ActivityKanban({
  activities,
  users = [],
  activityTypes = [],
  onAssign,
  onActivityClick,
  onActionClick,
  showCheckbox = false,
  selectedIds = new Set(),
  onSelectChange,
  showStatusActions = false,
  onStatusChange,
  className,
}: ActivityKanbanProps) {
  const activitiesByStatus = (status: Activity['status']) => 
    activities.filter(a => a.status === status);

  return (
    <div className={cn('grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4', className)}>
      {columns.map((column) => {
        const columnActivities = activitiesByStatus(column.status);
        return (
          <Card key={column.status} className="min-h-[400px]">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-gray-700">
                {column.label}
                <span className="ml-2 text-xs text-gray-500">
                  ({columnActivities.length})
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 overflow-y-auto max-h-[600px]">
              {columnActivities.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-8">Nessuna attività</p>
              ) : (
                columnActivities.map((activity) => (
                  <ActivityCard
                    key={activity.id}
                    activity={activity}
                    users={users}
                    onAssign={onAssign ? (userId) => onAssign(activity.id, userId) : undefined}
                    onDetailClick={() => onActivityClick?.(activity)}
                    showCheckbox={showCheckbox}
                    selected={selectedIds.has(activity.id)}
                    onSelectChange={onSelectChange ? (sel) => onSelectChange(activity.id, sel) : undefined}
                    showStatusActions={showStatusActions}
                    onStatusChange={onStatusChange ? (status) => onStatusChange(activity.id, status) : undefined}
                    actionType={getEffectiveActionType(activityTypes.find(t => t.code === activity.type))}
                    onActionClick={onActionClick ? () => {
                      const at = activityTypes.find(t => t.code === activity.type);
                      const eff = getEffectiveActionType(at);
                      if (eff) onActionClick(activity, eff);
                    } : undefined}
                    className="mb-2"
                  />
                ))
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}


